import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../lib/supabase';

// VB-019 — Onboarding state management via localStorage
// Deliberately kept separate from Zustand store to avoid coupling with business logic.

const KEY_WELCOME = 'vb-welcome-seen';
const KEY_CHECKLIST_DONE = 'vb-checklist-done';
const KEY_STEPS = 'vb-onboarding-steps-v1';

// VB-019 refonte — Guided tour keys
const KEY_TOUR_DONE = 'vb-tour-done-v1';
const KEY_TOUR_STEP = 'vb-tour-step-v1';

// View persistence
const KEY_LAST_VIEW = 'vb-last-view';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

export const onboardingStorage = {
  isWelcomeSeen: (): boolean => read<boolean>(KEY_WELCOME, false),
  setWelcomeSeen: (): void => write(KEY_WELCOME, true),

  isChecklistDone: (): boolean => read<boolean>(KEY_CHECKLIST_DONE, false),
  setChecklistDone: (): void => write(KEY_CHECKLIST_DONE, true),

  getCompletedSteps: (): Record<string, boolean> => read<Record<string, boolean>>(KEY_STEPS, {}),

  completeStep: (stepId: string): void => {
    const steps = onboardingStorage.getCompletedSteps();
    steps[stepId] = true;
    write(KEY_STEPS, steps);
  },

  // VB-019 refonte — Guided tour state
  isTourDone: (): boolean => read<boolean>(KEY_TOUR_DONE, false),
  setTourDone: (): void => write(KEY_TOUR_DONE, true),

  getTourStep: (): number => read<number>(KEY_TOUR_STEP, 0),
  setTourStep: (index: number): void => write(KEY_TOUR_STEP, index),

  // View persistence — restores the last visited page across refreshes
  getLastView: (): string | null => read<string | null>(KEY_LAST_VIEW, null),
  setLastView: (view: string): void => write(KEY_LAST_VIEW, view),

  /** For development/testing only — resets all onboarding state */
  reset: (): void => {
    try {
      localStorage.removeItem(KEY_WELCOME);
      localStorage.removeItem(KEY_CHECKLIST_DONE);
      localStorage.removeItem(KEY_STEPS);
      localStorage.removeItem(KEY_TOUR_DONE);
      localStorage.removeItem(KEY_TOUR_STEP);
      localStorage.removeItem(KEY_LAST_VIEW);
    } catch {}
  },
};

// ─── Phase 3.5 — New onboarding flow (Supabase-backed) ─────────────────────

interface OnboardingState {
  /** true si l'onboarding n'a pas ete complete (ou pas encore charge) */
  isOnboardingPending: boolean;
  /** true pendant le chargement initial */
  isLoading: boolean;
  /** Marque l'onboarding comme complete en base */
  completeOnboarding: () => Promise<void>;
  /** Passe a false sans ecrire en base (pour fermer temporairement) */
  dismissOnboarding: () => void;
}

export function useOnboarding(): OnboardingState {
  const session = useAuthStore((state) => state.session);
  const [isOnboardingPending, setIsOnboardingPending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!session?.user?.id) {
      setIsOnboardingPending(false);
      setIsLoading(false);
      return;
    }

    supabase
      .from('profiles')
      .select('onboarding_completed_at')
      .eq('id', session.user.id)
      .single()
      .then(({ data }) => {
        setIsOnboardingPending(!data?.onboarding_completed_at);
        setIsLoading(false);
      })
      .catch(() => {
        setIsOnboardingPending(false);
        setIsLoading(false);
      });
  }, [session?.user?.id]);

  const completeOnboarding = useCallback(async () => {
    if (!session?.user?.id) return;
    const { error } = await supabase
      .from('profiles')
      .update({ onboarding_completed_at: new Date().toISOString() })
      .eq('id', session.user.id);

    if (!error) {
      setIsOnboardingPending(false);
    }
  }, [session?.user?.id]);

  const dismissOnboarding = useCallback(() => {
    setIsOnboardingPending(false);
  }, []);

  return { isOnboardingPending, isLoading, completeOnboarding, dismissOnboarding };
}
